import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';

/**
 * Modul 1b (Otkup zlata — operativa). Fullscreen operativni pregled nad
 * bi_m1b_offers_unified + bi_m1b_buyback_unified (P2/P3). Vizualni jezik posudjen
 * iz 4.3 (goldStrategyModule): fixed inset 0, zIndex 4000, INK plava, kartice
 * #d8e2ec. Grafovi su crno-bijeli (sivi tonovi); spot linija je jedina boja.
 *
 * API: /api/ado-m1b/* (module_access:ado). Vidi App\Support\AdoModule1b\Pipeline.
 */

const INK = '#23405f';
const ACC = '#b1611e'; // jedina dopustena boja (spot linija)
const card: React.CSSProperties = { background: '#fff', border: '1px solid #d8e2ec', borderRadius: 12, padding: 16, marginBottom: 14 };
const BTN: React.CSSProperties = { padding: '10px 16px', background: INK, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' };
const BTN2: React.CSSProperties = { ...BTN, background: '#e7edf3', color: INK };
const INP: React.CSSProperties = { padding: '9px 11px', border: '1px solid #ccd6e0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };

const nf = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2 });
const nf4 = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 4 });
const eur = (n: number | null | undefined) => n === null || n === undefined ? '—' : `${nf.format(n)} €`;
const grams = (n: number | null | undefined) => n === null || n === undefined ? '—' : `${nf2.format(n)} g`;
const num = (n: number | null | undefined) => n === null || n === undefined ? '—' : nf.format(n);

type DayCell = { broj: number; eur: number; fine_g: number };
type Pulse = {
    range: { from: string; to: string };
    otkup: { danas: DayCell; jucer: DayCell; isti_dan_lani: DayCell; razdoblje_eur: number };
    izlaz: { danas: DayCell; jucer: DayCell; isti_dan_lani: DayCell; razdoblje_eur: number };
};
type BreakdownRow = { poslovnica: string; broj: number; eur: number; fine_g: number };
type TrendRow = { period: string; otkup_eur: number; otkup_fine_g: number; izlaz_eur: number; izlaz_fine_g: number };
type SpreadRow = { period: string; otkup_eur_g: number; spot_eur_g: number; spread_pct: number | null };
type HourRow = { dow: number; hour: number; broj: number; eur: number };
type YoyResp = { years: number[]; rows: Array<Record<string, number>> };
type Exposure = { otkup_fine_g: number; izlaz_fine_g: number; neto_fine_g: number };
type CoverageResp = {
    range: { from: string; to: string };
    offers: Array<{ source: string; broj: number; sa_artiklom: number; od: string | null; do: string | null }>;
    buyback: Array<{ source: string; broj: number; od: string | null; do: string | null }>;
    napomene: string[];
};

type View = 'pregled' | 'poslovnice' | 'trejding' | 'yoy' | 'satnica' | 'kvaliteta';

function StatCard({ label, value, sub, color = INK }: { label: string; value: string; sub?: string; color?: string }) {
    return (
        <div className="am-card" style={{ ...card, marginBottom: 0, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#5c6b7f' }}>{label}</span>
            <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.02em', color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            {sub ? <span style={{ fontSize: 11.5, color: '#8b98a9' }}>{sub}</span> : null}
        </div>
    );
}

function CardTitle({ children }: { children: React.ReactNode }) {
    return <strong style={{ fontSize: 14.5, color: INK, display: 'block', marginBottom: 10 }}>{children}</strong>;
}

/** Crno-bijeli grupirani stupci: otkup (tamnosivi) vs izlaz (svijetlosivi srafirani). */
function BarPair({ rows, valueKey1, valueKey2, label1, label2, fmt }: {
    rows: TrendRow[]; valueKey1: keyof TrendRow; valueKey2: keyof TrendRow; label1: string; label2: string; fmt: (n: number) => string;
}) {
    const max = Math.max(1, ...rows.map((r) => Math.max(Number(r[valueKey1]) || 0, Number(r[valueKey2]) || 0)));
    return (
        <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 12, color: '#5c6b7f' }}>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#4a5a6f', marginRight: 5, verticalAlign: 'middle' }} />{label1}</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'repeating-linear-gradient(45deg,#c2cbd6,#c2cbd6 3px,#fff 3px,#fff 6px)', border: '1px solid #c2cbd6', marginRight: 5, verticalAlign: 'middle' }} />{label2}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 200, overflowX: 'auto', paddingBottom: 4 }}>
                {rows.map((r) => {
                    const v1 = Number(r[valueKey1]) || 0;
                    const v2 = Number(r[valueKey2]) || 0;
                    return (
                        <div key={r.period} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 46 }} title={`${r.period}\n${label1}: ${fmt(v1)}\n${label2}: ${fmt(v2)}`}>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 170 }}>
                                <div style={{ width: 16, height: `${(v1 / max) * 100}%`, background: '#4a5a6f', minHeight: v1 > 0 ? 2 : 0 }} />
                                <div style={{ width: 16, height: `${(v2 / max) * 100}%`, background: 'repeating-linear-gradient(45deg,#c2cbd6,#c2cbd6 3px,#fff 3px,#fff 6px)', border: v2 > 0 ? '1px solid #c2cbd6' : 'none', boxSizing: 'border-box', minHeight: v2 > 0 ? 2 : 0 }} />
                            </div>
                            <span style={{ fontSize: 9.5, color: '#8b98a9', marginTop: 4, whiteSpace: 'nowrap', transform: 'rotate(-40deg)', transformOrigin: 'top left', height: 34 }}>{r.period}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** Spread graf: otkupna EUR/g (siva linija) i spot EUR/g (ACC, jedina boja). */
function SpreadChart({ rows }: { rows: SpreadRow[] }) {
    if (rows.length === 0) return <p style={{ color: '#8b98a9' }}>Nema podataka o cijeni po gramu (samo live otkup ima gram-razinu).</p>;
    const all = rows.flatMap((r) => [r.otkup_eur_g, r.spot_eur_g]).filter((n) => n > 0);
    const min = Math.min(...all) * 0.98;
    const max = Math.max(...all) * 1.02;
    const span = Math.max(0.0001, max - min);
    const W = Math.max(600, rows.length * 60);
    const H = 220;
    const x = (i: number) => (rows.length <= 1 ? W / 2 : (i / (rows.length - 1)) * (W - 40) + 20);
    const y = (v: number) => H - 24 - ((v - min) / span) * (H - 44);
    const line = (key: 'otkup_eur_g' | 'spot_eur_g') => rows.map((r, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(r[key])}`).join(' ');
    return (
        <div style={{ overflowX: 'auto' }}>
            <svg width={W} height={H} style={{ display: 'block' }}>
                <path d={line('otkup_eur_g')} fill="none" stroke="#4a5a6f" strokeWidth={2} />
                <path d={line('spot_eur_g')} fill="none" stroke={ACC} strokeWidth={2} strokeDasharray="5 3" />
                {rows.map((r, i) => (
                    <g key={r.period}>
                        <circle cx={x(i)} cy={y(r.otkup_eur_g)} r={3} fill="#4a5a6f" />
                        <circle cx={x(i)} cy={y(r.spot_eur_g)} r={3} fill={ACC} />
                        <text x={x(i)} y={H - 6} fontSize={9.5} fill="#8b98a9" textAnchor="middle">{r.period}</text>
                    </g>
                ))}
            </svg>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: '#5c6b7f' }}>
                <span><span style={{ display: 'inline-block', width: 18, height: 2, background: '#4a5a6f', marginRight: 5, verticalAlign: 'middle' }} />Otkupna EUR/g</span>
                <span><span style={{ display: 'inline-block', width: 18, height: 2, background: ACC, marginRight: 5, verticalAlign: 'middle' }} />Spot EUR/g</span>
            </div>
        </div>
    );
}

const DOW = ['Pon', 'Uto', 'Sri', 'Cet', 'Pet', 'Sub', 'Ned'];

export function AdoModule1b({ token, locale, onExit }: { token: string; locale: 'hr' | 'en'; onExit?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const today = new Date().toISOString().slice(0, 10);
    const [from, setFrom] = useState('2025-01-01');
    const [to, setTo] = useState(today);
    const [branch, setBranch] = useState('');
    const [gran, setGran] = useState<'dan' | 'tjedan' | 'mjesec' | 'godina'>('mjesec');
    const [view, setView] = useState<View>('pregled');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const [branches, setBranches] = useState<string[]>([]);
    const [pulse, setPulse] = useState<Pulse | null>(null);
    const [breakdown, setBreakdown] = useState<BreakdownRow[]>([]);
    const [trend, setTrend] = useState<TrendRow[]>([]);
    const [spread, setSpread] = useState<SpreadRow[]>([]);
    const [hours, setHours] = useState<HourRow[]>([]);
    const [yoy, setYoy] = useState<YoyResp | null>(null);
    const [exposure, setExposure] = useState<Exposure | null>(null);
    const [coverage, setCoverage] = useState<CoverageResp | null>(null);

    const qs = useMemo(() => {
        const p = new URLSearchParams({ date_from: from, date_to: to });
        if (branch) p.set('branch', branch);
        return p.toString();
    }, [from, to, branch]);

    const refresh = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const [p, b, tr, sp, hh, yy, ex, cov] = await Promise.all([
                api<Pulse>(`/ado-m1b/pulse?${qs}`, token),
                api<{ rows: BreakdownRow[] }>(`/ado-m1b/breakdown?${qs}`, token),
                api<{ rows: TrendRow[] }>(`/ado-m1b/trend?${qs}&granularity=${gran}`, token),
                api<{ rows: SpreadRow[] }>(`/ado-m1b/spread?${qs}&granularity=${gran}`, token),
                api<{ rows: HourRow[] }>(`/ado-m1b/by-hour?${qs}`, token),
                api<YoyResp>(`/ado-m1b/yoy?${qs}`, token),
                api<Exposure>(`/ado-m1b/exposure?${qs}`, token),
                api<CoverageResp>(`/ado-m1b/coverage?${qs}`, token),
            ]);
            setPulse(p);
            setBreakdown(b.rows);
            setTrend(tr.rows);
            setSpread(sp.rows);
            setHours(hh.rows);
            setYoy(yy);
            setExposure(ex);
            setCoverage(cov);
        } catch (e) {
            setError(e instanceof Error ? e.message : t('Dohvat nije uspio.', 'Fetch failed.'));
        } finally {
            setBusy(false);
        }
    }, [qs, gran, token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        api<{ branches: string[] }>('/ado-m1b/branches', token).then((r) => setBranches(r.branches)).catch(() => setBranches([]));
    }, [token]);

    useEffect(() => { void refresh(); }, [refresh]);

    // Auto puls svakih 60s (samo kad nije usred dohvata).
    useEffect(() => {
        if (busy) return;
        const timer = window.setTimeout(() => { void refresh(); }, 60000);
        return () => window.clearTimeout(timer);
    }, [busy, refresh]);

    // ESC izlaz.
    useEffect(() => {
        if (!onExit) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onExit(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onExit]);

    const tabs: Array<[View, string]> = [
        ['pregled', t('Pregled', 'Overview')],
        ['poslovnice', t('Poslovnice', 'Branches')],
        ['trejding', t('Trejding', 'Trading')],
        ['yoy', t('YoY', 'YoY')],
        ['satnica', t('Satnica', 'Hours')],
        ['kvaliteta', t('Kvaliteta podataka', 'Data quality')],
    ];
    const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 };

    const maxBranch = Math.max(1, ...breakdown.map((r) => r.eur));
    const maxHour = Math.max(1, ...hours.map((h) => h.broj));

    return (
        <div className="am-shell" style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#f3f5f8', color: INK, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <style>{`
                .am-shell { font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.45; }
                .am-shell button, .am-shell input, .am-shell select { font: inherit; }
                .am-shell button:focus-visible, .am-shell input:focus-visible, .am-shell select:focus-visible { outline: 3px solid rgba(35,64,95,.24); outline-offset: 2px; }
                .am-nav button.active { background: ${INK} !important; border-color: ${INK} !important; color: #fff !important; box-shadow: 0 4px 12px rgba(35,64,95,.18); }
                .am-card { transition: border-color .16s ease, box-shadow .16s ease; }
                .am-card:hover { border-color: #b8c1ce !important; box-shadow: 0 8px 20px rgba(23,32,51,.08); }
                .am-shell table { width: 100%; border-collapse: collapse; font-size: 13px; }
                .am-shell th { text-align: left; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; color: #8b98a9; padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
                .am-shell td { padding: 9px 10px; border-bottom: 1px solid #eef2f6; vertical-align: top; }
                .am-main { padding: 20px 24px; overflow-y: auto; }
                @media (max-width: 760px) { .am-main { padding: 14px !important; } }
            `}</style>

            <div style={{ background: '#fff', borderBottom: '1px solid #dfe4ea', padding: '18px 24px 0', boxShadow: '0 2px 10px rgba(23,32,51,.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', paddingBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9b6b16', fontWeight: 750 }}>Auro Domus · Modul 1.2 · {t('Otkup zlata', 'Gold buyback')}</div>
                        <div style={{ fontSize: 27, fontWeight: 750, letterSpacing: '-.025em', lineHeight: 1.15, marginTop: 2 }}>{t('Operativa otkupa', 'Buyback operations')}</div>
                        <div style={{ fontSize: 11.5, color: '#8b98a9', marginTop: 2 }}>{t('Razdoblje', 'Range')}: {from} → {to}{busy ? ` · ${t('učitavam…', 'loading…')}` : ''}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button type="button" style={BTN} disabled={busy} onClick={() => void refresh()}>{busy ? `⟳ ${t('Dohvat…', 'Fetching…')}` : `⟳ ${t('Osvježi', 'Refresh')}`}</button>
                        {onExit ? <button type="button" style={BTN2} onClick={onExit}>✕ {t('Zatvori', 'Close')} (ESC)</button> : null}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', paddingBottom: 12 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#5c6b7f' }}>{t('Od', 'From')}
                        <input type="date" style={INP} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#5c6b7f' }}>{t('Do', 'To')}
                        <input type="date" style={INP} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#5c6b7f' }}>{t('Poslovnica', 'Branch')}
                        <select style={INP} value={branch} onChange={(e) => setBranch(e.target.value)}>
                            <option value="">{t('Sve', 'All')}</option>
                            {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#5c6b7f' }}>{t('Granulacija', 'Granularity')}
                        <select style={INP} value={gran} onChange={(e) => setGran(e.target.value as typeof gran)}>
                            <option value="dan">{t('Dan', 'Day')}</option>
                            <option value="tjedan">{t('Tjedan', 'Week')}</option>
                            <option value="mjesec">{t('Mjesec', 'Month')}</option>
                            <option value="godina">{t('Godina', 'Year')}</option>
                        </select>
                    </label>
                </div>

                <div className="am-nav" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', paddingBottom: 12 }}>
                    {tabs.map(([key, label]) => (
                        <button key={key} type="button" className={view === key ? 'active' : ''} onClick={() => setView(key)}
                            style={{ background: '#fff', border: '1px solid #d5dbe3', cursor: 'pointer', padding: '8px 14px', borderRadius: 9, fontWeight: 650, color: '#445066' }}>{label}</button>
                    ))}
                </div>
            </div>

            <div className="am-main">
                {error ? <div style={{ ...card, borderColor: '#e6b8b8', background: '#fbf4f4', color: '#8a3232' }}>{error}</div> : null}

                {view === 'pregled' && pulse ? (
                    <>
                        <div style={{ ...grid, marginBottom: 14 }}>
                            <StatCard label={t('Otkup danas', 'Buyback today')} value={eur(pulse.otkup.danas.eur)} sub={`${num(pulse.otkup.danas.broj)} ${t('transakcija', 'txns')} · ${grams(pulse.otkup.danas.fine_g)}`} />
                            <StatCard label={t('Otkup jučer', 'Buyback yesterday')} value={eur(pulse.otkup.jucer.eur)} sub={`${num(pulse.otkup.jucer.broj)} · ${grams(pulse.otkup.jucer.fine_g)}`} />
                            <StatCard label={t('Isti dan lani', 'Same day last year')} value={eur(pulse.otkup.isti_dan_lani.eur)} sub={`${num(pulse.otkup.isti_dan_lani.broj)} · ${grams(pulse.otkup.isti_dan_lani.fine_g)}`} />
                            <StatCard label={t('Otkup u razdoblju', 'Buyback in range')} value={eur(pulse.otkup.razdoblje_eur)} color={ACC} />
                        </div>
                        <div style={{ ...grid, marginBottom: 14 }}>
                            <StatCard label={t('Izlaz danas', 'Outflow today')} value={eur(pulse.izlaz.danas.eur)} sub={`${num(pulse.izlaz.danas.broj)} · ${grams(pulse.izlaz.danas.fine_g)}`} />
                            <StatCard label={t('Izlaz jučer', 'Outflow yesterday')} value={eur(pulse.izlaz.jucer.eur)} sub={`${num(pulse.izlaz.jucer.broj)} · ${grams(pulse.izlaz.jucer.fine_g)}`} />
                            <StatCard label={t('Izlaz isti dan lani', 'Outflow same day last year')} value={eur(pulse.izlaz.isti_dan_lani.eur)} sub={`${num(pulse.izlaz.isti_dan_lani.broj)}`} />
                            <StatCard label={t('Izlaz u razdoblju', 'Outflow in range')} value={eur(pulse.izlaz.razdoblje_eur)} color={ACC} />
                        </div>
                        {exposure ? (
                            <div style={card}>
                                <CardTitle>{t('Fizička izloženost (fini grami)', 'Physical exposure (fine grams)')}</CardTitle>
                                <div style={grid}>
                                    <StatCard label={t('Otkupljeno', 'Bought')} value={grams(exposure.otkup_fine_g)} />
                                    <StatCard label={t('Izašlo (ponude)', 'Out (offers)')} value={grams(exposure.izlaz_fine_g)} />
                                    <StatCard label={t('Neto pozicija', 'Net position')} value={grams(exposure.neto_fine_g)} color={exposure.neto_fine_g >= 0 ? INK : ACC} />
                                </div>
                                <p style={{ fontSize: 11.5, color: '#8b98a9', marginTop: 10, marginBottom: 0 }}>
                                    {t('Gruba mjera: gram-razina postoji samo za live otkup (~2026-04 nadalje) i blagajna_json ponude. Nije zamjena za gram-usklađen inventar.',
                                       'Rough measure: gram-level detail exists only for live buyback (~2026-04 onward) and blagajna_json offers. Not a gram-matched inventory.')}
                                </p>
                            </div>
                        ) : null}
                    </>
                ) : null}

                {view === 'poslovnice' ? (
                    <div style={card}>
                        <CardTitle>{t('Otkup po poslovnici', 'Buyback by branch')}</CardTitle>
                        {breakdown.length === 0 ? <p style={{ color: '#8b98a9' }}>{t('Nema podataka.', 'No data.')}</p> : (
                            <table>
                                <thead><tr><th>{t('Poslovnica', 'Branch')}</th><th style={{ textAlign: 'right' }}>{t('Transakcija', 'Txns')}</th><th style={{ textAlign: 'right' }}>EUR</th><th style={{ textAlign: 'right' }}>{t('Fini g', 'Fine g')}</th><th style={{ width: '30%' }} /></tr></thead>
                                <tbody>
                                    {breakdown.map((r) => (
                                        <tr key={r.poslovnica}>
                                            <td>{r.poslovnica}</td>
                                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{num(r.broj)}</td>
                                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{eur(r.eur)}</td>
                                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nf2.format(r.fine_g)}</td>
                                            <td><div style={{ height: 12, background: '#4a5a6f', width: `${(r.eur / maxBranch) * 100}%`, minWidth: r.eur > 0 ? 2 : 0 }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ) : null}

                {view === 'trejding' ? (
                    <>
                        <div style={card}>
                            <CardTitle>{t('Trend otkup vs izlaz (EUR)', 'Trend buyback vs outflow (EUR)')}</CardTitle>
                            {trend.length === 0 ? <p style={{ color: '#8b98a9' }}>{t('Nema podataka.', 'No data.')}</p> :
                                <BarPair rows={trend} valueKey1="otkup_eur" valueKey2="izlaz_eur" label1={t('Otkup', 'Buyback')} label2={t('Izlaz', 'Outflow')} fmt={(n) => nf.format(n) + ' €'} />}
                        </div>
                        <div style={card}>
                            <CardTitle>{t('Otkupna cijena vs spot (EUR/g)', 'Buyback price vs spot (EUR/g)')}</CardTitle>
                            <SpreadChart rows={spread} />
                        </div>
                    </>
                ) : null}

                {view === 'yoy' && yoy ? (
                    <div style={card}>
                        <CardTitle>{t('Godina na godinu po mjesecu', 'Year over year by month')}</CardTitle>
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('Mjesec', 'Month')}</th>
                                    {yoy.years.map((y) => <th key={`o${y}`} style={{ textAlign: 'right' }}>{t('Otkup', 'Buyback')} {y}</th>)}
                                    {yoy.years.map((y) => <th key={`i${y}`} style={{ textAlign: 'right' }}>{t('Izlaz', 'Outflow')} {y}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {yoy.rows.map((r) => (
                                    <tr key={r.month}>
                                        <td>{String(r.month).padStart(2, '0')}</td>
                                        {yoy.years.map((y) => <td key={`o${y}`} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{eur(r[`otkup_${y}`])}</td>)}
                                        {yoy.years.map((y) => <td key={`i${y}`} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{eur(r[`izlaz_${y}`])}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                {view === 'satnica' ? (
                    <div style={card}>
                        <CardTitle>{t('Satni heatmap otkupa (broj transakcija)', 'Hourly buyback heatmap (txn count)')}</CardTitle>
                        {hours.length === 0 ? <p style={{ color: '#8b98a9' }}>{t('Nema podataka.', 'No data.')}</p> : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr><th /> {Array.from({ length: 24 }, (_, h) => <th key={h} style={{ textAlign: 'center', padding: '4px 3px', fontSize: 10 }}>{h}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {DOW.map((d, dow) => (
                                            <tr key={dow}>
                                                <td style={{ fontWeight: 700, fontSize: 12 }}>{d}</td>
                                                {Array.from({ length: 24 }, (_, h) => {
                                                    const cell = hours.find((x) => x.dow === dow && x.hour === h);
                                                    const v = cell?.broj ?? 0;
                                                    const alpha = v === 0 ? 0 : 0.15 + 0.85 * (v / maxHour);
                                                    return <td key={h} title={v ? `${d} ${h}h: ${v} (${eur(cell?.eur)})` : ''} style={{ padding: 0, border: '1px solid #eef2f6', width: 22, height: 22, background: v ? `rgba(35,64,95,${alpha})` : '#fff' }} />;
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : null}

                {view === 'kvaliteta' && coverage ? (
                    <>
                        <div style={card}>
                            <CardTitle>{t('Izvori ponuda (izlaz)', 'Offer sources (outflow)')}</CardTitle>
                            <table>
                                <thead><tr><th>{t('Izvor', 'Source')}</th><th style={{ textAlign: 'right' }}>{t('Redaka', 'Rows')}</th><th style={{ textAlign: 'right' }}>{t('S artiklom', 'With article')}</th><th>{t('Od', 'From')}</th><th>{t('Do', 'To')}</th></tr></thead>
                                <tbody>
                                    {coverage.offers.map((r) => (
                                        <tr key={r.source}>
                                            <td>{r.source}</td>
                                            <td style={{ textAlign: 'right' }}>{num(r.broj)}</td>
                                            <td style={{ textAlign: 'right' }}>{num(r.sa_artiklom)}</td>
                                            <td>{r.od ? String(r.od).slice(0, 10) : '—'}</td>
                                            <td>{r.do ? String(r.do).slice(0, 10) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={card}>
                            <CardTitle>{t('Izvori otkupa (ulaz)', 'Buyback sources (inflow)')}</CardTitle>
                            <table>
                                <thead><tr><th>{t('Izvor', 'Source')}</th><th style={{ textAlign: 'right' }}>{t('Redaka', 'Rows')}</th><th>{t('Od', 'From')}</th><th>{t('Do', 'To')}</th></tr></thead>
                                <tbody>
                                    {coverage.buyback.map((r) => (
                                        <tr key={r.source}>
                                            <td>{r.source}</td>
                                            <td style={{ textAlign: 'right' }}>{num(r.broj)}</td>
                                            <td>{r.od ? String(r.od).slice(0, 10) : '—'}</td>
                                            <td>{r.do ? String(r.do).slice(0, 10) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={card}>
                            <CardTitle>{t('Napomene o granularnosti', 'Granularity notes')}</CardTitle>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#445066' }}>
                                {coverage.napomene.map((n, i) => <li key={i} style={{ marginBottom: 4 }}>{n}</li>)}
                            </ul>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
